import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import api from '../services/api';
import editorService from '../services/editorService';
import aiService from '../services/aiService';
import mediaService from '../services/mediaService';
import { uploadContentFile, uploadToUrl } from '../services/uploadService';
import StoryStudio from '../components/story/StoryStudio';
import { useFeature } from '../hooks/useFeature';
import { trackFeature } from '../utils/analytics';
import {
  DocumentTextIcon,
  PlusIcon,
  XMarkIcon,
  CloudArrowUpIcon,
  VideoCameraIcon,
  PaintBrushIcon,
  PhotoIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  PencilSquareIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FunnelIcon,
  TrashIcon,
  PaperAirplaneIcon,
  ClockIcon,
  ArrowPathIcon,
  EllipsisVerticalIcon,
  EyeIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { PLATFORMS } from '../constants/platforms';
import PublishSettingsModal from '../components/publish/PublishSettingsModal';
import VideoFrameCapture from '../components/shared/VideoFrameCapture';
import toast from 'react-hot-toast';

// Platform brand meta for quick lookup
const PLATFORM_META = PLATFORMS.reduce((acc, p) => {
  acc[p.value] = p;
  return acc;
}, {});

// Content type options
const CONTENT_TYPES = [
  { value: 'VIDEO',    label: 'Video',    accept: 'video/*',  icon: VideoCameraIcon },
  { value: 'IMAGE',    label: 'Image',    accept: 'image/*',  icon: PhotoIcon },
  { value: 'TEXT',     label: 'Text Post', accept: '',        icon: DocumentTextIcon },
  { value: 'REEL',     label: 'Reel',     accept: 'video/*',  icon: VideoCameraIcon },
  { value: 'SHORT',    label: 'Short',    accept: 'video/*',  icon: VideoCameraIcon },
  { value: 'STORY',    label: 'Story',    accept: 'image/*',  icon: PhotoIcon },
  { value: 'CAROUSEL', label: 'Carousel', accept: 'image/*',  icon: PhotoIcon },
];

// Carousel is live to all tiers (Phase 4 wired the Instagram multi-asset publish path).
const CAROUSEL_LIVE = true;

// Per-content-type media spec. Fetched from GET /content/media-spec at selection time; this local
// map mirrors the backend (MediaSpecCatalog) and is the instant/offline fallback so the uploader
// always has a spec to render from. STORY accepting image OR video is the Story-video fix going live.
const DEFAULT_SPECS = {
  VIDEO:    { minFiles: 1, maxFiles: 1,  kinds: ['VIDEO'],          maxSizeMb: 500, maxDurationSec: null, aspect: null,   reorderable: false, multiple: false, requiresMedia: true,  accept: 'video/*' },
  IMAGE:    { minFiles: 1, maxFiles: 1,  kinds: ['IMAGE'],          maxSizeMb: 30,  maxDurationSec: null, aspect: null,   reorderable: false, multiple: false, requiresMedia: true,  accept: 'image/*' },
  TEXT:     { minFiles: 0, maxFiles: 0,  kinds: [],                 maxSizeMb: 0,   maxDurationSec: null, aspect: null,   reorderable: false, multiple: false, requiresMedia: false, accept: '' },
  REEL:     { minFiles: 1, maxFiles: 1,  kinds: ['VIDEO'],          maxSizeMb: 500, maxDurationSec: 90,   aspect: '9:16', reorderable: false, multiple: false, requiresMedia: true,  accept: 'video/*' },
  SHORT:    { minFiles: 1, maxFiles: 1,  kinds: ['VIDEO'],          maxSizeMb: 500, maxDurationSec: 180,  aspect: '9:16', reorderable: false, multiple: false, requiresMedia: true,  accept: 'video/*' },
  STORY:    { minFiles: 1, maxFiles: 1,  kinds: ['IMAGE', 'VIDEO'], maxSizeMb: 100, maxDurationSec: 60,   aspect: '9:16', reorderable: false, multiple: false, requiresMedia: true,  accept: 'image/*,video/*' },
  CAROUSEL: { minFiles: 2, maxFiles: 10, kinds: ['IMAGE', 'VIDEO'], maxSizeMb: 100, maxDurationSec: null, aspect: null,   reorderable: true,  multiple: true,  requiresMedia: true,  accept: 'image/*,video/*' },
};

// The asset kind ('IMAGE' | 'VIDEO') for a File, from its MIME type.
const kindOf = (file) => (file?.type?.startsWith('video/') ? 'VIDEO' : 'IMAGE');

// Human label for the uploader, from what kinds the spec accepts.
const specMediaLabel = (spec) => {
  const img = spec.kinds.includes('IMAGE');
  const vid = spec.kinds.includes('VIDEO');
  if (img && vid) return 'Image or Video';
  if (vid) return 'Video';
  return 'Image';
};

// The small grey hint under the dropzone, derived from the spec.
const specHint = (spec) => {
  const kinds = spec.kinds.includes('VIDEO') && spec.kinds.includes('IMAGE')
    ? 'Images or videos'
    : spec.kinds.includes('VIDEO') ? 'MP4, MOV' : 'JPG, PNG, GIF';
  const parts = [`${kinds} · max ${spec.maxSizeMb}MB`];
  if (spec.maxDurationSec) parts.push(`up to ${spec.maxDurationSec}s`);
  if (spec.aspect) parts.push(`${spec.aspect} recommended`);
  return parts.join(' · ');
};

// A draft whose content type needs a media file, but none has been uploaded yet
// (e.g. an Idea Studio "Use this" draft — a script/idea with no video). These have
// no duration or thumbnail to show and can't be published until media is added.
const needsMedia = (item) =>
  item.status === 'DRAFT' && item.contentType !== 'TEXT' && !item.s3Url;

// Thumbnail to show in list cards. Prefer an explicit thumbnailUrl; otherwise fall back to the
// media itself when it's an image (e.g. a flattened STORY, which is its own poster) so published
// image content never renders as a bare placeholder icon. Video media has no still to show here.
const isVideoUrl = (u) => !!u && /\.(mp4|mov|webm|m4v)(\?|$)/i.test(u);
const cardThumb = (item) => {
  if (item.thumbnailUrl) return item.thumbnailUrl;
  const isVideoType = ['VIDEO', 'REEL', 'SHORT'].includes(item.contentType);
  if (item.s3Url && !isVideoType && !isVideoUrl(item.s3Url)) return item.s3Url;
  return null;
};

// FB-style story mode chooser. Facebook splits "Create a story" into two entry cards — a photo/video
// story and a text story — so the user picks intent up front instead of guessing whether the file is
// required. Both paths end in Story Studio; "text" simply skips the upload (Story Studio flattens the
// typed text on a background into the image that publishes as a photo story).
const StoryModeChooser = ({ onPick }) => (
  <div>
    <label className="label">What kind of story?</label>
    <div className="grid grid-cols-2 gap-3">
      <button
        type="button"
        onClick={() => onPick('media')}
        className="group relative flex flex-col items-center justify-center gap-3 rounded-2xl p-6 text-white
          bg-gradient-to-br from-violet-600 via-indigo-500 to-sky-400 min-h-[150px]
          ring-2 ring-transparent hover:ring-white/60 focus:outline-none focus:ring-white transition-all"
      >
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white shadow">
          <PhotoIcon className="h-6 w-6 text-gray-900" />
        </span>
        <span className="text-sm font-semibold text-center leading-snug">Create a photo<br />or video story</span>
      </button>
      <button
        type="button"
        onClick={() => onPick('text')}
        className="group relative flex flex-col items-center justify-center gap-3 rounded-2xl p-6 text-white
          bg-gradient-to-br from-fuchsia-500 via-pink-500 to-rose-500 min-h-[150px]
          ring-2 ring-transparent hover:ring-white/60 focus:outline-none focus:ring-white transition-all"
      >
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white shadow font-serif font-bold text-gray-900 text-lg leading-none">
          Aa
        </span>
        <span className="text-sm font-semibold text-center leading-snug">Create a<br />text story</span>
      </button>
    </div>
  </div>
);

// Small amber chip flagging a draft that still needs its media file uploaded.
const NeedsMediaChip = () => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium
    bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
    <ExclamationCircleIcon className="w-3 h-3" /> Needs media
  </span>
);

const StatusBadge = ({ status }) => {
  const map = {
    PUBLISHED:            'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    PARTIALLY_PUBLISHED:  'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    SCHEDULED:            'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    READY:                'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400',
    PROCESSING:           'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    UPLOADING:            'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    DRAFT:                'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
    FAILED:               'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    PUBLISHING:           'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  };
  const label = {
    PARTIALLY_PUBLISHED: 'Partial',
    READY: 'Ready to publish',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${map[status] || map.DRAFT}`}>
      {label[status] || status}
    </span>
  );
};

// Platform column chips: the platform's brand pill (icon + name) with an inline per-platform
// publish-status glyph (✓ completed / ! failed / spinner in progress). This replaces the separate
// status row that used to sit under the title — the platform column now carries both name + status.
const PlatformChips = ({ platforms = [], jobs = [] }) => {
  if (platforms.length === 0) return <span className="text-gray-400 text-xs">—</span>;
  const jobByPlatform = {};
  jobs.forEach((j) => { if (j.platform && !jobByPlatform[j.platform]) jobByPlatform[j.platform] = j; });
  return (
    <div className="flex flex-wrap gap-1">
      {platforms.map((p) => {
        const meta = PLATFORM_META[p] || {};
        const Icon = meta.icon;
        const job = jobByPlatform[p];
        const ok = job?.status === 'COMPLETED';
        const failed = job?.status === 'FAILED';
        const processing = job?.status === 'PROCESSING' || job?.status === 'QUEUED';
        return (
          <span
            key={p}
            title={job ? (job.errorMessage || job.status) : undefined}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium
              ${meta.bgColor || 'bg-gray-100 dark:bg-gray-800'} ${meta.color || 'text-gray-600'}`}
          >
            {Icon && <Icon className="text-sm" />}
            {meta.label || p}
            {ok && <CheckCircleIcon className="w-3 h-3 text-green-600 dark:text-green-400" />}
            {failed && <ExclamationCircleIcon className="w-3 h-3 text-red-600 dark:text-red-400" />}
            {processing && <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin opacity-70" />}
          </span>
        );
      })}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Upload Modal
// ─────────────────────────────────────────────────────────────────────────────
const UploadModal = ({ onClose, onCreated, onReadyToPublish, onDesignStory, prefill }) => {
  const [step, setStep]     = useState(1); // 1=type, 2=platforms+details
  const [data, setData]     = useState({
    title: prefill?.title || '',
    description: prefill?.description || '',
    contentType: 'VIDEO',
    selectedPlatforms: [],
    file: null,
    thumbnailBlob: null,
  });
  // The platform an Idea Studio idea targeted — auto-selected in step 2 once it's
  // confirmed connected & compatible with the chosen format.
  const desiredPlatform = prefill?.platform || null;
  const [capturedThumbnailUrl, setCapturedThumbnailUrl] = useState(null);
  const [compatiblePlatforms, setCompatiblePlatforms] = useState([]);   // connected + compatible
  const [expiredPlatforms, setExpiredPlatforms]       = useState([]);   // expired/inactive connections
  const [allCompatible, setAllCompatible]             = useState([]);   // all that support this type
  const [platformsState, setPlatformsState]           = useState('idle'); // idle | loading | ready | error
  const [platformsError, setPlatformsError]           = useState(null);
  const [dragActive, setDragActive]                   = useState(false);
  const [uploading, setUploading]                     = useState(false);
  const [fileDuration, setFileDuration]               = useState(null);
  // STORY only: which FB-style mode the user picked — null (not chosen yet), 'media' (photo/video
  // story) or 'text' (text-on-background story). Facebook splits these into two entry cards; we
  // mirror that so a text story isn't hidden behind a file dropzone it doesn't need.
  const [storyMode, setStoryMode]                     = useState(null);
  // Media spec for the chosen type (drives accept, size/duration hints, single-vs-multi uploader).
  const [spec, setSpec]     = useState(DEFAULT_SPECS[prefill?.contentType] || DEFAULT_SPECS.VIDEO);
  // Carousel staging: files chosen locally, uploaded (append) in order on submit.
  const [slides, setSlides] = useState([]); // [{ localId, file, previewUrl, kind }]

  // Revoke slide object-URLs when the modal unmounts (removeSlide handles the per-slide case).
  const slidesRef = useRef(slides);
  slidesRef.current = slides;
  useEffect(() => () => slidesRef.current.forEach(s => URL.revokeObjectURL(s.previewUrl)), []);

  // ── AI title/description generator (paid feature, gated by the ai_metadata_generator flag) ──
  const { enabled: aiEnabled } = useFeature('ai_metadata_generator');
  const [aiOpen, setAiOpen]         = useState(false);
  const [aiBrief, setAiBrief]       = useState('');
  const [aiLoading, setAiLoading]   = useState(false);
  const [aiGenerated, setAiGenerated] = useState(false); // enables "Undo" + "Regenerate" labels
  const preAiMeta = useRef(null);                          // title/description before generation, for Undo

  // Map the selected platform keys (connection ids or names) back to platform names for the prompt.
  const resolvePlatformNames = () => {
    const names = data.selectedPlatforms
      .map((key) => compatiblePlatforms.find((cp) => (cp.connectionId || cp.id || cp.platform) === key)?.platform)
      .filter(Boolean);
    return [...new Set(names)];
  };

  // Open the brief panel, pre-seeded from whatever the user has already typed.
  const openAiPanel = () => {
    const seed = [data.title, data.description].filter(Boolean).join(' — ');
    setAiBrief(seed);
    setAiOpen(true);
  };

  const runAiGenerate = async () => {
    if (aiBrief.trim().length < 10) { toast.error('Add a few more words about your content first'); return; }
    try {
      setAiLoading(true);
      const platforms = resolvePlatformNames();
      const result = await aiService.generateContentMetadata({
        brief: aiBrief.trim(),
        contentType: data.contentType,
        platforms: platforms.length ? platforms : ['GENERAL'],
      });
      preAiMeta.current = { title: data.title, description: data.description };
      setData((d) => ({ ...d, title: result.title, description: result.description }));
      setAiGenerated(true);
      toast.success('Title & description generated');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'AI generation is unavailable — you can still write your own');
    } finally {
      setAiLoading(false);
    }
  };

  const undoAiGenerate = () => {
    if (!preAiMeta.current) return;
    setData((d) => ({ ...d, title: preAiMeta.current.title, description: preAiMeta.current.description }));
    preAiMeta.current = null;
    setAiGenerated(false);
  };

  // Fetch compatible connected platforms whenever content type changes
  const loadPlatforms = useCallback(async (contentType) => {
    setPlatformsState('loading');
    setPlatformsError(null);
    setCompatiblePlatforms([]);
    setExpiredPlatforms([]);
    setAllCompatible([]);
    setData(d => ({ ...d, selectedPlatforms: [] }));
    try {
      const res = await api.get('/api/v1/content/compatible-platforms', { params: { contentType } });
      const payload = res.data?.data || {};
      const connected = Array.isArray(payload.connected) ? payload.connected : [];
      const allCompatList = Array.isArray(payload.allCompatible) ? payload.allCompatible : [];
      const expiredList = Array.isArray(payload.expired) ? payload.expired : [];
      setCompatiblePlatforms(connected);
      setExpiredPlatforms(expiredList);
      setAllCompatible(allCompatList);
      // Pre-select the idea's target platform when it's connected & supports this format.
      if (desiredPlatform) {
        const match = connected.find(cp => cp.platform === desiredPlatform);
        if (match) setData(d => ({ ...d, selectedPlatforms: [match.connectionId || match.id || match.platform] }));
      }
      setPlatformsState('ready');
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to load platforms';
      console.error('[ContentPage] loadPlatforms error:', err);
      setPlatformsError(msg);
      setPlatformsState('error');
    }
  }, [desiredPlatform]);

  const handleSelectType = (typeValue) => {
    // Local spec immediately (instant render), then refresh from the authoritative server spec.
    setSpec(DEFAULT_SPECS[typeValue] || DEFAULT_SPECS.VIDEO);
    slidesRef.current.forEach(s => URL.revokeObjectURL(s.previewUrl));
    setSlides([]);
    setFileDuration(null);
    setData(d => ({ ...d, contentType: typeValue, file: null, selectedPlatforms: [] }));
    setStoryMode(null); // re-ask the photo/video-vs-text choice whenever the type changes
    loadPlatforms(typeValue);
    setStep(2);
    mediaService.getSpec(typeValue).then(s => { if (s) setSpec(s); }).catch(() => {});
  };

  const togglePlatform = (platformKey, connectionId) => {
    setData(d => {
      // If connectionId is available, use it for selection tracking
      const key = connectionId || platformKey;
      const already = d.selectedPlatforms.includes(key);
      return {
        ...d,
        selectedPlatforms: already
          ? d.selectedPlatforms.filter(p => p !== key)
          : [...d.selectedPlatforms, key],
      };
    });
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) handleFileSelect(e.dataTransfer.files[0]);
  };

  // Validate a file against the current spec (kind + size). Returns null when ok, else an error string.
  const validateAgainstSpec = (file) => {
    if (!spec.kinds.includes(kindOf(file))) {
      return `Please choose ${specMediaLabel(spec).toLowerCase()} for a ${data.contentType.toLowerCase()}`;
    }
    if (file.size > spec.maxSizeMb * 1024 * 1024) {
      return `File too large. Max ${spec.maxSizeMb}MB`;
    }
    return null;
  };

  const handleFileSelect = (file) => {
    const err = validateAgainstSpec(file);
    if (err) { toast.error(err); return; }
    setData(d => ({ ...d, file }));
    // Extract duration for any video (platform validation, e.g. TikTok/Story max duration).
    if (file.type.startsWith('video/')) {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.src = URL.createObjectURL(file);
      video.onloadedmetadata = () => {
        setFileDuration(video.duration);
        URL.revokeObjectURL(video.src);
      };
    } else {
      setFileDuration(null);
    }
    toast.success(`Selected: ${file.name}`);
  };

  // ── Carousel slide staging (multi-asset types) ──────────────────────────────
  const addSlides = (fileList) => {
    const incoming = Array.from(fileList || []);
    setSlides(prev => {
      const next = [...prev];
      for (const file of incoming) {
        if (next.length >= spec.maxFiles) { toast.error(`Up to ${spec.maxFiles} slides`); break; }
        const err = validateAgainstSpec(file);
        if (err) { toast.error(`${file.name}: ${err}`); continue; }
        next.push({
          localId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          file,
          previewUrl: URL.createObjectURL(file),
          kind: kindOf(file),
        });
      }
      return next;
    });
  };

  const removeSlide = (localId) => {
    setSlides(prev => {
      const target = prev.find(s => s.localId === localId);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter(s => s.localId !== localId);
    });
  };

  const moveSlide = (localId, dir) => {
    setSlides(prev => {
      const idx = prev.findIndex(s => s.localId === localId);
      const swap = idx + dir;
      if (idx < 0 || swap < 0 || swap >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[swap]] = [next[swap], next[idx]];
      return next;
    });
  };

  const [uploadProgress, setUploadProgress] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!data.title.trim()) { toast.error('Please enter a title'); return; }
    if (data.selectedPlatforms.length === 0) { toast.error('Select at least one platform'); return; }
    if (spec.multiple) {
      if (slides.length < spec.minFiles) { toast.error(`Add at least ${spec.minFiles} slides`); return; }
    } else if (spec.requiresMedia && !data.file && data.contentType !== 'STORY') {
      toast.error('Please select a file to upload'); return;
    }

    setUploading(true);
    try {
      // Step 1: Create content item (DRAFT)
      // Separate connectionIds (UUIDs) from platform strings for backward compat
      const isUUID = (s) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
      const connectionIds = data.selectedPlatforms.filter(isUUID);
      const platformStrings = data.selectedPlatforms.filter(s => !isUUID(s));
      // Resolve connectionIds back to platform names for targetPlatforms
      const resolvedPlatforms = connectionIds.map(id => {
        const cp = compatiblePlatforms.find(c => (c.connectionId || c.id) === id);
        return cp?.platform;
      }).filter(Boolean);
      const allPlatforms = [...new Set([...platformStrings, ...resolvedPlatforms])];

      setUploadProgress('Creating content…');
      const createRes = await api.post('/api/v1/content', {
        title: data.title,
        description: data.description,
        contentType: data.contentType,
        targetPlatforms: allPlatforms,
        ...(connectionIds.length > 0 && { targetConnectionIds: connectionIds }),
      });
      const contentItem = createRes.data.data;
      const contentId = contentItem.id;
      let uploadedItem = null; // set after upload-complete; carries s3Url for Story Studio

      if (spec.multiple) {
        // Carousel: upload each staged slide in order, then attach it (append) to the item.
        for (let i = 0; i < slides.length; i++) {
          const slide = slides[i];
          setUploadProgress(`Uploading slide ${i + 1} of ${slides.length}…`);
          const mime = slide.file.type || (slide.kind === 'VIDEO' ? 'video/mp4' : 'image/jpeg');
          const { uploadUrl, s3Key } = await mediaService.getSlideUploadUrl(contentId, mime);
          await uploadToUrl(uploadUrl, slide.file, { contentType: mime });
          await mediaService.appendMedia(contentId, {
            s3Key, kind: slide.kind, sizeBytes: slide.file.size,
          });
        }
      } else if (data.contentType !== 'TEXT' && data.file) {
        // Step 2+3: Upload to S3 — single PUT for small files, resumable multipart for large ones.
        // Reports real progress and throws on any storage error (no more silently-stuck uploads).
        setUploadProgress('Uploading file… 0%');
        const s3Key = await uploadContentFile(contentId, data.file, {
          contentType: data.file.type || 'video/mp4',
          onProgress: (p) => setUploadProgress(`Uploading file… ${p}%`),
        });

        // Step 4: Mark upload complete → PROCESSING
        setUploadProgress('Processing…');
        const completeParams = { s3Key, fileSizeBytes: data.file.size };
        if (fileDuration != null) completeParams.durationSeconds = Math.round(fileDuration);
        const completeRes = await api.put(`/api/v1/content/${contentId}/upload-complete`, null, {
          params: completeParams,
        });
        uploadedItem = completeRes.data.data; // has s3Url — used as the Story Studio background

        // Step 4b: Upload captured thumbnail if available
        if (data.thumbnailBlob) {
          setUploadProgress('Uploading thumbnail…');
          const thumbRes = await api.post(`/api/v1/content/${contentId}/thumbnail-upload-url`);
          const { uploadUrl: thumbUploadUrl, publicUrl: thumbPublicUrl } = thumbRes.data.data;
          await uploadToUrl(thumbUploadUrl, data.thumbnailBlob, { contentType: 'image/jpeg' });
          await api.put(`/api/v1/content/${contentId}`, { thumbnailUrl: thumbPublicUrl });
        }
      }

      setUploadProgress('');
      setUploading(false);

      // STORY → open Story Studio to design it (background, text, fonts, stickers), then publish.
      // Works whether a photo/video was uploaded (becomes the background) or nothing was (a text
      // story on a colour/gradient — Story Studio flattens it to an image, which publishes as a
      // photo story on Facebook/Instagram).
      if (data.contentType === 'STORY') {
        onDesignStory({
          item: uploadedItem || contentItem,
          platforms: allPlatforms,
          connectionIds,
          content: { title: data.title, description: data.description, contentType: 'STORY' },
          fileDuration,
        });
        return;
      }

      // Step 5: Open publish settings modal for platform-specific configuration
      onReadyToPublish({
        contentId,
        platforms: allPlatforms,
        connectionIds,
        content: {
          title: data.title,
          description: data.description,
          contentType: data.contentType,
        },
        fileDuration,
      });
      onClose();
    } catch (err) {
      // Prefer the API's message; fall back to an upload error's own message (uploadService throws
      // plain Errors like "Upload rejected by storage (HTTP 403)") before the generic fallback.
      toast.error(err.response?.data?.message || err.message || 'Failed to publish content');
    } finally {
      setUploading(false);
      setUploadProgress('');
    }
  };

  // A STORY must first have a mode chosen; a text story needs no file, a photo/video story does.
  const storyReady = data.contentType === 'STORY'
    && (storyMode === 'text' || (storyMode === 'media' && !!data.file));
  const canSubmit = data.title.trim()
    && data.selectedPlatforms.length > 0
    && platformsState === 'ready'
    && (spec.multiple ? slides.length >= spec.minFiles
        : data.contentType === 'STORY' ? storyReady
        : (!spec.requiresMedia || !!data.file));

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={() => !uploading && onClose()}
      className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-0 md:p-4"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 16 }}
        onClick={e => e.stopPropagation()}
        className="bg-white dark:bg-dark-900 rounded-t-2xl md:rounded-2xl shadow-2xl w-full max-w-none md:max-w-2xl max-h-[92vh] flex flex-col pb-[env(safe-area-inset-bottom)] md:pb-0"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200 dark:border-dark-700 flex-shrink-0">
          <div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">Upload New Content</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {step === 1 ? 'Step 1 — Choose content type' : `Step 2 — Details & platforms (${data.contentType})`}
            </p>
          </div>
          <button
            onClick={() => !uploading && onClose()}
            disabled={uploading}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-800 transition-colors disabled:opacity-50"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1">
          {/* Step 1 — Content Type */}
          {step === 1 && (
            <div className="p-6">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Select the type of content you want to upload. Compatible platforms will be shown next.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {CONTENT_TYPES.filter(t => t.value !== 'CAROUSEL' || CAROUSEL_LIVE).map(({ value, label, icon: Icon }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => handleSelectType(value)}
                    className="flex flex-col items-center gap-2 p-4 rounded-xl border-2
                      border-gray-200 dark:border-dark-700
                      hover:border-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/20
                      transition-all group"
                  >
                    <Icon className="w-8 h-8 text-gray-400 group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2 — Details + Platform multi-select */}
          {step === 2 && (
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Back to type */}
              <button
                type="button"
                onClick={() => setStep(1)}
                className="text-sm text-violet-600 dark:text-violet-400 hover:underline flex items-center gap-1"
              >
                ← Change content type
              </button>

              {/* Title */}
              <div>
                <div className="flex items-center justify-between">
                  <label className="label">Title *</label>
                  {aiEnabled && (
                    <button
                      type="button"
                      onClick={() => (aiOpen ? setAiOpen(false) : openAiPanel())}
                      disabled={uploading}
                      className="text-xs font-semibold text-violet-600 dark:text-violet-400 inline-flex items-center gap-1 hover:underline disabled:opacity-50"
                    >
                      <SparklesIcon className="w-4 h-4" /> Generate with AI
                    </button>
                  )}
                </div>
                <input
                  type="text"
                  value={data.title}
                  onChange={e => setData(d => ({ ...d, title: e.target.value }))}
                  placeholder="Enter content title"
                  className="input"
                  required
                  disabled={uploading}
                />

                {/* AI brief panel — generates title + description together from a short brief */}
                {aiEnabled && aiOpen && (
                  <div className="mt-2 rounded-xl border border-violet-200 dark:border-violet-900/50 bg-violet-50/50 dark:bg-violet-900/10 p-3 space-y-2">
                    <label className="block text-[11px] font-semibold uppercase tracking-wide text-violet-700 dark:text-violet-300">
                      What&apos;s this about?
                    </label>
                    <textarea
                      value={aiBrief}
                      onChange={e => setAiBrief(e.target.value)}
                      rows={2}
                      placeholder="e.g. A 3-minute studio tour for beginner creators — lighting, camera, the desk build"
                      className="input resize-none text-sm"
                      disabled={aiLoading}
                    />
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={runAiGenerate}
                        disabled={aiLoading || aiBrief.trim().length < 10}
                        className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-gradient-to-r from-pink-500 to-violet-600 text-white disabled:opacity-50 inline-flex items-center gap-1"
                      >
                        {aiLoading ? 'Generating…' : (aiGenerated ? 'Regenerate' : 'Generate')}
                      </button>
                      {aiGenerated && preAiMeta.current && (
                        <button type="button" onClick={undoAiGenerate}
                          className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                          Undo
                        </button>
                      )}
                      <button type="button" onClick={() => setAiOpen(false)}
                        className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 ml-auto">
                        Close
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="label">Description</label>
                <textarea
                  value={data.description}
                  onChange={e => setData(d => ({ ...d, description: e.target.value }))}
                  placeholder="Enter content description"
                  rows={3}
                  className="input resize-none"
                  disabled={uploading}
                />
              </div>

              {/* Platform multi-select */}
              <div>
                <label className="label">
                  Target Platforms *
                  <span className="ml-2 text-xs font-normal text-gray-400">
                    (your connected platforms that support {data.contentType})
                  </span>
                </label>

                {/* Loading */}
                {platformsState === 'loading' && (
                  <div className="flex items-center gap-2 py-5 text-gray-400 text-sm">
                    <div className="spinner w-4 h-4" />
                    Loading compatible platforms…
                  </div>
                )}

                {/* API Error */}
                {platformsState === 'error' && (
                  <div className="rounded-xl border border-red-200 bg-red-50 dark:bg-red-900/20 p-4 space-y-3">
                    <div className="flex items-start gap-2 text-sm text-red-700 dark:text-red-400">
                      <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium">Could not load platforms</p>
                        <p className="text-xs mt-0.5 opacity-80">{platformsError}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => loadPlatforms(data.contentType)}
                      className="text-xs font-medium text-red-700 dark:text-red-400 underline hover:no-underline"
                    >
                      Retry
                    </button>
                  </div>
                )}

                {/* Ready — no connected platforms for this type */}
                {platformsState === 'ready' && compatiblePlatforms.length === 0 && (
                  <div className="rounded-xl border border-dashed border-yellow-300 bg-yellow-50 dark:bg-yellow-900/20 p-4 space-y-3">
                    <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                      None of your connected platforms support <strong>{data.contentType}</strong> content.
                    </p>
                    {allCompatible.length > 0 && (
                      <div>
                        <p className="text-xs text-yellow-700 dark:text-yellow-400 mb-2">
                          The following platforms support this content type — connect one in Settings:
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {allCompatible.map(key => {
                            const meta = PLATFORM_META[key] || {};
                            const Icon = meta.icon;
                            return (
                              <span key={key}
                                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium
                                  ${meta.bgColor || 'bg-gray-100 dark:bg-gray-800'} ${meta.color || 'text-gray-600'}`}>
                                {Icon && <Icon />}
                                {meta.label || key}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <a
                      href="/dashboard/settings"
                      className="inline-flex items-center gap-1 text-xs font-medium text-yellow-800 dark:text-yellow-300 underline hover:no-underline"
                    >
                      Connect a platform — in Settings or the Analytics Platforms card →
                    </a>
                  </div>
                )}

                {/* Ready — has compatible connected platforms */}
                {platformsState === 'ready' && compatiblePlatforms.length > 0 && (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {compatiblePlatforms.map((cp) => {
                        const meta = PLATFORM_META[cp.platform] || {};
                        const Icon = meta.icon;
                        const selKey = cp.connectionId || cp.id || cp.platform;
                        const selected = data.selectedPlatforms.includes(selKey);
                        return (
                          <button
                            key={selKey}
                            type="button"
                            onClick={() => togglePlatform(cp.platform, cp.connectionId || cp.id)}
                            disabled={uploading}
                            className={`flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all
                              ${selected
                                ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20 shadow-sm'
                                : 'border-gray-200 dark:border-dark-700 hover:border-violet-300 dark:hover:border-violet-600'}`}
                          >
                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0
                              ${meta.bgColor || 'bg-gray-100 dark:bg-gray-800'}`}>
                              {Icon && <Icon className={`text-xl ${meta.color}`} />}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                                {cp.displayName || meta.label || cp.platform}
                              </p>
                              {cp.platformUsername && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                  @{cp.platformUsername}
                                </p>
                              )}
                            </div>
                            {selected && (
                              <CheckCircleIcon className="w-5 h-5 text-violet-600 dark:text-violet-400 ml-auto flex-shrink-0" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                    {data.selectedPlatforms.length > 0 && (
                      <p className="text-xs text-fuchsia-600 dark:text-fuchsia-400 mt-2 font-medium">
                        {data.selectedPlatforms.length} account{data.selectedPlatforms.length > 1 ? 's' : ''} selected
                      </p>
                    )}
                  </>
                )}

                {/* Expired / inactive connections — reconnect prompt */}
                {platformsState === 'ready' && expiredPlatforms.length > 0 && (
                  <div className="mt-3 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4 space-y-3">
                    <div className="flex items-start gap-2 text-sm text-amber-800 dark:text-amber-200">
                      <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <p className="font-medium">
                        {expiredPlatforms.length === 1 ? 'A connected platform needs' : 'Some connected platforms need'} reconnection
                      </p>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {expiredPlatforms.map((ep) => {
                        const meta = PLATFORM_META[ep.platform] || {};
                        const Icon = meta.icon;
                        return (
                          <div
                            key={ep.connectionId || ep.id}
                            className="flex items-center gap-3 p-3 rounded-xl border-2 border-amber-300 dark:border-amber-700 opacity-70"
                          >
                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0
                              ${meta.bgColor || 'bg-gray-100 dark:bg-gray-800'}`}>
                              {Icon && <Icon className={`text-xl ${meta.color}`} />}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                                {ep.displayName || meta.label || ep.platform}
                              </p>
                              <p className="text-xs text-amber-600 dark:text-amber-400">
                                {ep.status === 'EXPIRED' ? 'Token expired' : 'Disconnected'}
                              </p>
                            </div>
                            <ArrowPathIcon className="w-4 h-4 text-amber-600 dark:text-amber-400 ml-auto flex-shrink-0" />
                          </div>
                        );
                      })}
                    </div>
                    <a
                      href="/dashboard/settings"
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-800 dark:text-amber-300 hover:underline"
                    >
                      <ArrowPathIcon className="w-3.5 h-3.5" />
                      Reconnect in Settings
                    </a>
                  </div>
                )}
              </div>

              {/* STORY: ask photo/video-vs-text first (mirrors Facebook's two story cards). The media
                  dropzone below is shown only once the user chooses the photo/video mode. */}
              {data.contentType === 'STORY' && !storyMode && (
                <StoryModeChooser onPick={setStoryMode} />
              )}
              {data.contentType === 'STORY' && storyMode === 'text' && (
                <div className="rounded-xl border border-dashed border-fuchsia-200 dark:border-fuchsia-900/50 bg-fuchsia-50 dark:bg-fuchsia-900/10 p-4">
                  <div className="flex items-start gap-3">
                    <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-fuchsia-100 dark:bg-fuchsia-900/30 font-serif font-bold text-fuchsia-700 dark:text-fuchsia-300">Aa</span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-200">Text story — no upload needed</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">You'll type your text and pick a background in the next step.</p>
                    </div>
                    <button type="button" onClick={() => setStoryMode(null)}
                      className="ml-auto flex-shrink-0 text-xs font-medium text-fuchsia-700 dark:text-fuchsia-300 hover:underline">Change</button>
                  </div>
                </div>
              )}

              {data.contentType === 'STORY' && storyMode === 'media' && (
                <div className="flex items-center justify-between -mb-2">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Photo or video story</span>
                  <button type="button" onClick={() => { setStoryMode(null); setData(d => ({ ...d, file: null })); }}
                    className="text-xs font-medium text-violet-600 dark:text-violet-400 hover:underline">Change</button>
                </div>
              )}

              {/* Media upload — spec-driven. A slide tray for multi-asset types (carousel),
                  otherwise a single dropzone. Hidden entirely for types with no media (TEXT), and for
                  a STORY until the user picks the photo/video mode above. */}
              {spec.requiresMedia && (data.contentType !== 'STORY' || storyMode === 'media') && (spec.multiple ? (
                <div>
                  <label className="label">
                    Slides * <span className="text-xs font-normal text-gray-400">({slides.length}/{spec.maxFiles}, min {spec.minFiles})</span>
                  </label>
                  {slides.length > 0 && (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-3">
                      {slides.map((slide, i) => (
                        <div key={slide.localId} className="relative group rounded-lg overflow-hidden border border-gray-200 dark:border-dark-700 bg-gray-50 dark:bg-dark-800">
                          <div className="aspect-square flex items-center justify-center">
                            {slide.kind === 'VIDEO'
                              ? <video src={slide.previewUrl} className="w-full h-full object-cover" muted />
                              : <img src={slide.previewUrl} alt={`Slide ${i + 1}`} className="w-full h-full object-cover" />}
                          </div>
                          <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-black/60 text-white text-[10px] font-medium">{i + 1}</span>
                          <div className="absolute inset-x-0 bottom-0 flex justify-between p-1 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                            <button type="button" disabled={uploading || i === 0} onClick={() => moveSlide(slide.localId, -1)}
                              className="p-0.5 rounded bg-white/90 disabled:opacity-30" title="Move left">
                              <ChevronLeftIcon className="w-3.5 h-3.5 text-gray-800" />
                            </button>
                            <button type="button" disabled={uploading} onClick={() => removeSlide(slide.localId)}
                              className="p-0.5 rounded bg-white/90" title="Remove">
                              <XMarkIcon className="w-3.5 h-3.5 text-red-600" />
                            </button>
                            <button type="button" disabled={uploading || i === slides.length - 1} onClick={() => moveSlide(slide.localId, 1)}
                              className="p-0.5 rounded bg-white/90 disabled:opacity-30" title="Move right">
                              <ChevronRightIcon className="w-3.5 h-3.5 text-gray-800" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {slides.length < spec.maxFiles && (
                    <div className="relative border-2 border-dashed rounded-xl p-6 text-center border-gray-300 dark:border-dark-600 hover:border-violet-400 cursor-pointer">
                      <input
                        type="file"
                        multiple
                        onChange={e => { if (e.target.files?.length) addSlides(e.target.files); e.target.value = ''; }}
                        accept={spec.accept}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        disabled={uploading}
                      />
                      <PlusIcon className="w-8 h-8 mx-auto text-gray-400" />
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Add slides</p>
                      <p className="text-xs text-gray-400">{specHint(spec)}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <label className="label">{specMediaLabel(spec)} File *</label>
                  <div
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer
                      ${dragActive
                        ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20'
                        : 'border-gray-300 dark:border-dark-600 hover:border-violet-400'}`}
                  >
                    {/* No `capture` attribute: it forces mobile straight into the camera instead of
                        the file picker. The native picker still offers the camera as one option,
                        alongside Photo Library and Files — which is what "click to browse" implies. */}
                    <input
                      type="file"
                      onChange={e => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                      accept={spec.accept}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      disabled={uploading}
                    />
                    {data.file ? (
                      <div className="space-y-1">
                        <CloudArrowUpIcon className="w-10 h-10 mx-auto text-fuchsia-500" />
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{data.file.name}</p>
                        <p className="text-xs text-gray-500">{(data.file.size / 1024 / 1024).toFixed(2)} MB</p>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <CloudArrowUpIcon className="w-10 h-10 mx-auto text-gray-400" />
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Drag & drop or click to browse
                        </p>
                        <p className="text-xs text-gray-400">{specHint(spec)}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {/* Thumbnail capture from video frames */}
              {(data.contentType === 'VIDEO' || data.contentType === 'REEL' || data.contentType === 'SHORT') && (
                data.file ? (
                  <VideoFrameCapture
                    videoFile={data.file}
                    onCapture={(blob) => {
                      setData(d => ({ ...d, thumbnailBlob: blob }));
                      if (capturedThumbnailUrl) URL.revokeObjectURL(capturedThumbnailUrl);
                      setCapturedThumbnailUrl(URL.createObjectURL(blob));
                      toast.success('Thumbnail captured!');
                    }}
                    capturedUrl={capturedThumbnailUrl}
                    disabled={uploading}
                  />
                ) : (
                  <div className="rounded-xl border border-dashed border-gray-200 dark:border-dark-700 p-4 text-center">
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      Upload your video first to capture a thumbnail frame
                    </p>
                  </div>
                )
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={uploading}
                  className="btn btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading || !canSubmit}
                  className="btn flex-1 text-white bg-gradient-to-r from-pink-500 to-violet-600 hover:from-pink-600 hover:to-violet-700 shadow-md"
                >
                  {uploading ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="spinner w-4 h-4" />
                      {uploadProgress || 'Working…'}
                    </span>
                  ) : data.contentType === 'STORY' ? (
                    'Design story →'
                  ) : (
                    `Publish to ${data.selectedPlatforms.length || '?'} platform${data.selectedPlatforms.length !== 1 ? 's' : ''}`
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Schedule Modal
// ─────────────────────────────────────────────────────────────────────────────
const ScheduleModal = ({ item, onClose, onScheduled }) => {
  const [dateTime, setDateTime] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSchedule = async (e) => {
    e.preventDefault();
    if (!dateTime) { toast.error('Please select a date and time'); return; }
    const scheduledAt = new Date(dateTime).toISOString();
    if (new Date(scheduledAt) <= new Date()) { toast.error('Scheduled time must be in the future'); return; }
    setSaving(true);
    try {
      const res = await api.post(`/api/v1/content/${item.id}/schedule`, null, {
        params: { at: scheduledAt },
      });
      onScheduled(res.data.data);
      trackFeature('content_schedule');
      toast.success('Content scheduled!');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to schedule');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={() => !saving && onClose()}
      className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-0 md:p-4"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 16 }}
        onClick={e => e.stopPropagation()}
        className="bg-white dark:bg-dark-900 rounded-t-2xl md:rounded-2xl shadow-2xl w-full max-w-none md:max-w-md max-h-[92vh] overflow-y-auto pb-[env(safe-area-inset-bottom)] md:pb-0"
      >
        <div className="px-6 py-5 border-b border-gray-200 dark:border-dark-700">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">Schedule Publish</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 truncate">{item.title}</p>
        </div>
        <form onSubmit={handleSchedule} className="p-6 space-y-5">
          <div>
            <label className="label">Publish Date & Time *</label>
            <input
              type="datetime-local"
              value={dateTime}
              onChange={e => setDateTime(e.target.value)}
              className="input w-full"
              required
              disabled={saving}
              min={new Date().toISOString().slice(0, 16)}
            />
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} disabled={saving} className="btn btn-secondary flex-1">Cancel</button>
            <button type="submit" disabled={saving || !dateTime} className="btn flex-1 text-white bg-gradient-to-r from-pink-500 to-violet-600 hover:from-pink-600 hover:to-violet-700 shadow-md">
              {saving ? <span className="flex items-center justify-center gap-2"><div className="spinner w-4 h-4" />Scheduling…</span> : 'Schedule'}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Content Row Actions
// ─────────────────────────────────────────────────────────────────────────────
const ContentActions = ({ item, onEdit, onPreview, onUpdated, onDeleted, onPublish }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const btnRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState({});
  const [showStory, setShowStory] = useState(false);

  const toggleMenu = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      // Position the dropdown above or below depending on available space
      const spaceBelow = window.innerHeight - rect.bottom;
      const menuHeight = 200; // approximate max menu height
      if (spaceBelow < menuHeight && rect.top > menuHeight) {
        // Open upward
        setMenuStyle({ bottom: window.innerHeight - rect.top + 4, right: window.innerWidth - rect.right });
      } else {
        // Open downward (default)
        setMenuStyle({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
      }
    }
    setOpen(!open);
  };

  const handlePublishNow = () => {
    setOpen(false);
    // If file hasn't been uploaded yet (DRAFT with no s3Url), can't publish
    if (!item.s3Url && item.contentType !== 'TEXT') {
      toast.error('File not uploaded yet — re-upload this content');
      return;
    }
    const platforms = item.targetPlatforms || [item.targetPlatform].filter(Boolean);
    onPublish({
      contentId: item.id,
      platforms,
      content: {
        title: item.title,
        description: item.description,
        contentType: item.contentType,
        hashtags: item.hashtags,
        aiCaption: item.aiCaption,
      },
    });
  };

  const handleRetryFailed = async () => {
    setOpen(false);
    setBusy(true);
    try {
      const failedPlatforms = (item.platformJobs || [])
        .filter(j => j.status === 'FAILED')
        .map(j => (PLATFORM_META[j.platform] || {}).label || j.platform);
      const toastId = toast.loading(`Retrying ${failedPlatforms.join(', ')}…`);
      const res = await api.post(`/api/v1/content/${item.id}/retry-failed`);
      const updated = res.data.data;
      onUpdated(updated);
      // Show per-platform results
      const jobs = updated.platformJobs || [];
      const succeeded = jobs.filter(j => j.status === 'COMPLETED');
      const stillFailed = jobs.filter(j => j.status === 'FAILED');
      if (stillFailed.length === 0) {
        toast.success(`All platforms published successfully: ${succeeded.map(j => (PLATFORM_META[j.platform] || {}).label || j.platform).join(', ')}`, { id: toastId, duration: 5000 });
      } else {
        toast(`Still failed: ${stillFailed.map(j => (PLATFORM_META[j.platform] || {}).label || j.platform).join(', ')}${stillFailed[0]?.errorMessage ? ' — ' + stillFailed[0].errorMessage.substring(0, 80) : ''}`, { id: toastId, icon: '❌', duration: 8000 });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Retry failed');
    } finally {
      setBusy(false);
    }
  };

  const handleCancelSchedule = async () => {
    setOpen(false);
    setBusy(true);
    try {
      const res = await api.delete(`/api/v1/content/${item.id}/schedule`);
      onUpdated(res.data.data);
      toast.success('Schedule cancelled');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to cancel schedule');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    setOpen(false);
    if (!window.confirm(`Delete "${item.title}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/api/v1/content/${item.id}`);
      onDeleted(item.id);
      toast.success('Content deleted');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete');
    } finally {
      setBusy(false);
    }
  };

  const handleEditVideo = async () => {
    setOpen(false);
    setBusy(true);
    try {
      const project = await editorService.createProject({
        title: `Edit: ${item.title}`,
        contentItemId: item.id,
      });
      navigate(`/dashboard/editor/${project.id}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to open editor');
    } finally {
      setBusy(false);
    }
  };

  const canPublish = ['DRAFT', 'PROCESSING', 'READY'].includes(item.status);
  const canRetryFailed = ['FAILED', 'PARTIALLY_PUBLISHED'].includes(item.status);
  const canSchedule = ['DRAFT', 'PROCESSING', 'READY'].includes(item.status);
  const canCancelSchedule = item.status === 'SCHEDULED';
  // READY = uploaded but publish setup incomplete — surface a prominent resume action
  const isResumable = item.status === 'READY';

  const isVideoType = ['VIDEO', 'REEL', 'SHORT'].includes(item.contentType);

  return (
    <div className="relative">
      <div className="flex items-center gap-1">
        {isResumable && (
          <button
            onClick={handlePublishNow}
            title="Continue publish setup — your upload is saved"
            disabled={busy}
            className="px-2.5 py-1 rounded-lg text-xs font-semibold text-sky-700 bg-sky-50 hover:bg-sky-100
              dark:text-sky-400 dark:bg-sky-900/20 dark:hover:bg-sky-900/40 transition-colors disabled:opacity-50 flex items-center gap-1"
          >
            <PaperAirplaneIcon className="w-3.5 h-3.5" /> Continue setup
          </button>
        )}
        <button
          onClick={() => onPreview?.(item)}
          title="Preview"
          disabled={busy}
          className="p-2 rounded-lg border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800
            text-gray-600 dark:text-gray-300 shadow-sm
            hover:text-sky-600 hover:border-sky-300 hover:bg-sky-50
            dark:hover:text-sky-400 dark:hover:border-sky-700 dark:hover:bg-sky-900/25
            transition-colors disabled:opacity-50"
        >
          <EyeIcon className="w-[18px] h-[18px]" />
        </button>
        <button
          onClick={() => onEdit(item)}
          title="Edit"
          disabled={busy}
          className="p-2 rounded-lg border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800
            text-gray-600 dark:text-gray-300 shadow-sm
            hover:text-violet-600 hover:border-violet-300 hover:bg-violet-50
            dark:hover:text-violet-400 dark:hover:border-violet-700 dark:hover:bg-violet-900/25
            transition-colors disabled:opacity-50"
        >
          <PencilSquareIcon className="w-[18px] h-[18px]" />
        </button>
        {isVideoType && item.s3Url && (
          <button
            onClick={handleEditVideo}
            title="Open in Video Editor"
            disabled={busy}
            className="p-2 rounded-lg border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800
              text-gray-600 dark:text-gray-300 shadow-sm
              hover:text-purple-600 hover:border-purple-300 hover:bg-purple-50
              dark:hover:text-purple-400 dark:hover:border-purple-700 dark:hover:bg-purple-900/25
              transition-colors disabled:opacity-50"
          >
            <VideoCameraIcon className="w-[18px] h-[18px]" />
          </button>
        )}
        <button
          ref={btnRef}
          onClick={toggleMenu}
          title="More actions"
          disabled={busy}
          className="p-2 rounded-lg border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800
            text-gray-600 dark:text-gray-300 shadow-sm
            hover:text-gray-900 hover:border-gray-300 hover:bg-gray-50
            dark:hover:text-white dark:hover:border-dark-500 dark:hover:bg-dark-700
            transition-colors disabled:opacity-50"
        >
          {busy ? <div className="spinner w-[18px] h-[18px]" /> : <EllipsisVerticalIcon className="w-[18px] h-[18px]" />}
        </button>
      </div>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className="fixed z-50 bg-white dark:bg-dark-800 rounded-xl shadow-xl border border-gray-200 dark:border-dark-700 py-1 min-w-[180px]"
            style={menuStyle}
          >
            {canPublish && (
              <button onClick={handlePublishNow}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700 flex items-center gap-2">
                <PaperAirplaneIcon className="w-4 h-4" /> {isResumable ? 'Continue setup' : 'Publish Now'}
              </button>
            )}
            {canSchedule && (
              <button onClick={() => { setOpen(false); onEdit(item, 'schedule'); }}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700 flex items-center gap-2">
                <ClockIcon className="w-4 h-4" /> Schedule
              </button>
            )}
            {canCancelSchedule && (
              <button onClick={handleCancelSchedule}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700 flex items-center gap-2">
                <XMarkIcon className="w-4 h-4" /> Cancel Schedule
              </button>
            )}
            {canRetryFailed && (
              <button onClick={handleRetryFailed}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700 flex items-center gap-2">
                <ArrowPathIcon className="w-4 h-4" /> Retry Failed Platforms
              </button>
            )}
            {['VIDEO', 'REEL', 'SHORT'].includes(item.contentType) && item.s3Url && (
              <button onClick={handleEditVideo}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700 flex items-center gap-2">
                <VideoCameraIcon className="w-4 h-4" /> Edit Video
              </button>
            )}
            {item.contentType === 'STORY' && (
              <button onClick={() => { setOpen(false); setShowStory(true); }}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700 flex items-center gap-2">
                <PaintBrushIcon className="w-4 h-4" /> Design story
              </button>
            )}
            <div className="border-t border-gray-100 dark:border-dark-700 my-1" />
            <button onClick={handleDelete}
              className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2">
              <TrashIcon className="w-4 h-4" /> Delete
            </button>
          </div>
        </>
      )}
      {showStory && (
        <StoryStudio
          item={item}
          onClose={() => setShowStory(false)}
          onSaved={() => { setShowStory(false); onUpdated?.(); }}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Edit Modal
// ─────────────────────────────────────────────────────────────────────────────
const STATUS_NOTE = {
  PUBLISHED: {
    color: 'yellow',
    msg: 'Published content — metadata changes update your local record only. The content on the platform is not affected.',
  },
  PARTIALLY_PUBLISHED: {
    color: 'orange',
    msg: 'Some platforms failed to publish. You can retry the failed platforms from the actions menu.',
  },
  SCHEDULED: {
    color: 'blue',
    msg: 'Scheduled content — edits to title and description are saved locally and will not change the scheduled post.',
  },
  FAILED: {
    color: 'red',
    msg: 'This content failed to publish. You can edit the details and retry.',
  },
};

const EditModal = ({ item, onClose, onUpdated }) => {
  // hashtags is a comma-separated String on the backend (entity + DTO in/out), NOT an array.
  const [form, setForm] = useState({
    title: item.title || '',
    description: item.description || '',
    hashtags: item.hashtags || '',
  });
  const [saving, setSaving] = useState(false);

  const isDirty =
    form.title !== (item.title || '') ||
    form.description !== (item.description || '') ||
    form.hashtags !== (item.hashtags || '');

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      // Normalise to a clean comma-separated String — UpdateContentRequest.hashtags is a String;
      // sending an array makes Jackson reject the body with "Malformed request body" (400).
      const hashtags = form.hashtags.split(',').map(h => h.trim()).filter(Boolean).join(', ');
      const res = await api.put(`/api/v1/content/${item.id}`, {
        title: form.title.trim(),
        description: form.description.trim() || null,
        hashtags,
      });
      onUpdated(res.data.data);
      trackFeature('content_edit_metadata');
      toast.success('Content updated');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update content');
    } finally {
      setSaving(false);
    }
  };

  const statusNote = STATUS_NOTE[item.status];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={() => !saving && onClose()}
      className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-0 md:p-4"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 16 }}
        onClick={e => e.stopPropagation()}
        className="bg-white dark:bg-dark-900 rounded-t-2xl md:rounded-2xl shadow-2xl w-full max-w-none md:max-w-lg max-h-[92vh] md:max-h-[90vh] flex flex-col pb-[env(safe-area-inset-bottom)] md:pb-0"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200 dark:border-dark-700 flex-shrink-0">
          <div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">Edit Content</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 truncate max-w-xs">{item.title}</p>
          </div>
          <button
            onClick={() => !saving && onClose()}
            disabled={saving}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-800 transition-colors disabled:opacity-50"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSave} className="overflow-y-auto flex-1 p-6 space-y-5">
          {/* Status banner */}
          {statusNote && (
            <div className={`flex items-start gap-2 p-3 rounded-xl text-sm
              ${statusNote.color === 'yellow'
                ? 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300'
                : statusNote.color === 'blue'
                  ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300'
                  : statusNote.color === 'orange'
                    ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-800 dark:text-orange-300'
                    : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300'}`}
            >
              <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <p>{statusNote.msg}</p>
            </div>
          )}

          {/* Status + Type */}
          <div className="flex items-center gap-3">
            <StatusBadge status={item.status} />
            <span className="text-xs text-gray-400 dark:text-gray-500">{item.contentType}</span>
          </div>

          {/* Title */}
          <div>
            <label className="label">Title *</label>
            <input
              type="text"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              className="input"
              required
              disabled={saving}
            />
          </div>

          {/* Description */}
          <div>
            <label className="label">Description</label>
            <textarea
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              rows={3}
              className="input resize-none"
              disabled={saving}
              placeholder="Enter a description…"
            />
          </div>

          {/* Hashtags */}
          <div>
            <label className="label">
              Hashtags
              <span className="ml-2 text-xs font-normal text-gray-400">comma-separated</span>
            </label>
            <input
              type="text"
              value={form.hashtags}
              onChange={e => setForm(f => ({ ...f, hashtags: e.target.value }))}
              className="input"
              disabled={saving}
              placeholder="#creativecontent, #socialmedia"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} disabled={saving} className="btn btn-secondary flex-1">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !isDirty || !form.title.trim()}
              className="btn flex-1 text-white bg-gradient-to-r from-pink-500 to-violet-600 hover:from-pink-600 hover:to-violet-700 shadow-md"
            >
              {saving ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="spinner w-4 h-4" />
                  Saving…
                </span>
              ) : 'Save Changes'}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Preview Modal — a read-only look at what's inside a content item before publishing.
// For uploaded media it plays the video / shows the image; for a script-only draft
// (e.g. an Idea Studio idea with no video yet) it shows the hook + caption and makes
// clear that duration/preview only appear once media is added. This is the answer to
// "there's no way to check the actual content before publishing".
// ─────────────────────────────────────────────────────────────────────────────
const PreviewModal = ({ item, onClose }) => {
  const platforms = item.targetPlatforms?.length
    ? item.targetPlatforms
    : item.targetPlatform ? [item.targetPlatform] : [];
  const isVideo = ['VIDEO', 'REEL', 'SHORT'].includes(item.contentType);
  const isImage = ['IMAGE', 'STORY', 'CAROUSEL'].includes(item.contentType);
  const mediaMissing = needsMedia(item);
  // hashtags is a comma-separated String on the backend — split for chip display.
  const hashtags = (item.hashtags || '').split(',').map(h => h.trim()).filter(Boolean);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-0 md:p-4"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 16 }}
        onClick={e => e.stopPropagation()}
        className="bg-white dark:bg-dark-900 rounded-t-2xl md:rounded-2xl shadow-2xl w-full max-w-none md:max-w-lg max-h-[92vh] md:max-h-[90vh] flex flex-col pb-[env(safe-area-inset-bottom)] md:pb-0"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200 dark:border-dark-700 flex-shrink-0">
          <div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">Preview</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 truncate max-w-xs">{item.title}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-800 transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-5">
          {/* Media */}
          {item.s3Url && isVideo ? (
            <video
              src={item.s3Url}
              controls
              poster={item.thumbnailUrl || undefined}
              className="w-full rounded-xl bg-black max-h-[45vh]"
            />
          ) : item.s3Url && isImage ? (
            <img
              src={item.s3Url}
              alt=""
              className="w-full rounded-xl object-contain max-h-[45vh] bg-gray-100 dark:bg-dark-800"
            />
          ) : (
            <div className="rounded-xl border border-dashed border-gray-300 dark:border-dark-600 bg-gray-50 dark:bg-dark-800 p-8 text-center">
              {isVideo
                ? <VideoCameraIcon className="w-8 h-8 mx-auto text-gray-400 dark:text-gray-500" />
                : <PhotoIcon className="w-8 h-8 mx-auto text-gray-400 dark:text-gray-500" />}
              <p className="mt-2 text-sm font-medium text-gray-700 dark:text-gray-300">No media added yet</p>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {mediaMissing
                  ? 'This is a script/idea draft. Duration and preview appear once you add your video — and media is required before it can be published.'
                  : 'There is no uploaded file for this item.'}
              </p>
            </div>
          )}

          {/* Status / type / needs-media */}
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={item.status} />
            <span className="text-xs text-gray-400 dark:text-gray-500">{item.contentType}</span>
            {mediaMissing && <NeedsMediaChip />}
          </div>

          {/* Title (hook) */}
          <div>
            <p className="label">Title</p>
            <p className="text-base font-semibold text-gray-900 dark:text-white break-words">{item.title}</p>
          </div>

          {/* Caption / description */}
          <div>
            <p className="label">Caption</p>
            {item.description
              ? <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">{item.description}</p>
              : <p className="text-sm text-gray-400 dark:text-gray-500 italic">No caption</p>}
          </div>

          {/* Hashtags */}
          {hashtags.length > 0 && (
            <div>
              <p className="label">Hashtags</p>
              <div className="flex flex-wrap gap-1.5">
                {hashtags.map((h, i) => (
                  <span key={i} className="px-2 py-0.5 rounded-full text-xs bg-violet-50 text-violet-700 dark:bg-violet-900/25 dark:text-violet-300">
                    {h.startsWith('#') ? h : `#${h}`}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Platforms */}
          <div>
            <p className="label">Platforms</p>
            <PlatformChips platforms={platforms} />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-dark-700 flex-shrink-0">
          <button onClick={onClose} className="btn btn-secondary w-full">Close</button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────────
const PAGE_SIZE = 20;

const ContentPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadPrefill, setUploadPrefill]     = useState(null); // {title, description, platform} from Idea Studio
  const [editingItem, setEditingItem]         = useState(null);
  const [schedulingItem, setSchedulingItem]   = useState(null);
  const [previewItem, setPreviewItem]         = useState(null);

  // ── Publish settings modal state ────────────────────────────────────────────
  const [publishModalData, setPublishModalData] = useState(null); // { contentId, platforms, content }
  const [storyDesign, setStoryDesign] = useState(null); // { item, platforms, connectionIds, content, fileDuration } from the create wizard

  // ── List state ──────────────────────────────────────────────────────────────
  const [content, setContent]               = useState([]);
  const [page, setPage]                     = useState(0);
  const [totalElements, setTotalElements]   = useState(0);
  const [totalPages, setTotalPages]         = useState(0);
  const [loading, setLoading]               = useState(true);

  // ── Filter state ─────────────────────────────────────────────────────────────
  // searchInput is the raw controlled value; query.search is the debounced version
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState({
    search: '', status: '', contentType: '', platform: '', sort: 'createdAt,desc',
  });

  // ── Active platform connections (for platform filter dropdown) ─────────────
  const [connections, setConnections] = useState([]);
  useEffect(() => {
    api.get('/api/v1/platforms/connections')
      .then(res => {
        const all = res.data?.data || [];
        const active = all.filter(c => c.status === 'ACTIVE');
        setConnections(active);
      })
      .catch(() => setConnections([]));
  }, []);

  // Debounce: push search → query after 300 ms idle, reset to page 0
  useEffect(() => {
    const t = setTimeout(() => {
      setQuery(q => ({ ...q, search: searchInput.trim() }));
      setPage(0);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Filter helper — any filter change also resets to page 0
  const setFilter = (key, value) => {
    setQuery(q => ({ ...q, [key]: value }));
    setPage(0);
  };

  // ── Refresh handler ───────────────────────────────────────────────────────
  const [refreshKey, setRefreshKey] = useState(0);
  const isAutoRefresh = useRef(false);
  const handleRefresh = () => {
    isAutoRefresh.current = false;
    setRefreshKey(k => k + 1);
  };

  // ── Auto-poll when any content is in a transient state (PUBLISHING, UPLOADING, PROCESSING) ──
  useEffect(() => {
    const hasTransient = content.some(c =>
      ['PUBLISHING', 'UPLOADING', 'PROCESSING'].includes(c.status));
    if (!hasTransient) return;
    const interval = setInterval(() => {
      isAutoRefresh.current = true;
      setRefreshKey(k => k + 1);
    }, 5000);
    return () => clearInterval(interval);
  }, [content]);

  // ── Fetch ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    // Only show skeleton on initial load / user-driven changes, not auto-polls
    if (!isAutoRefresh.current) setLoading(true);
    isAutoRefresh.current = false;

    const params = { page, size: PAGE_SIZE };
    if (query.search)      params.search      = query.search;
    if (query.status)      params.status      = query.status;
    if (query.contentType) params.contentType = query.contentType;
    if (query.platform)    params.platform    = query.platform;
    if (query.sort)        params.sort        = query.sort;

    api.get('/api/v1/content', { params })
      .then(res => {
        if (cancelled) return;
        const d = res.data?.data || {};
        setContent(d.content || []);
        setTotalElements(d.totalElements || 0);
        setTotalPages(d.totalPages || 0);
      })
      .catch(() => {
        if (cancelled) return;
        setContent([]);
        setTotalElements(0);
        setTotalPages(0);
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [page, query, refreshKey]);

  // ── Deep-link: ?open=<contentId> opens the publish flow directly ─────────────
  // Used by the Video Editor's "Use in a Post" bridge — the edited video is
  // already a READY content item, so we jump straight into platform + caption.
  const deepLinkHandled = useRef(false);
  useEffect(() => {
    const openId = searchParams.get('open');
    if (!openId || deepLinkHandled.current) return;
    deepLinkHandled.current = true;

    const openPublish = (item) => {
      if (!item) { toast.error('That content could not be found'); return; }
      setPublishModalData({
        contentId: item.id,
        platforms: item.targetPlatforms || [item.targetPlatform].filter(Boolean),
        content: {
          title: item.title,
          description: item.description,
          contentType: item.contentType,
          hashtags: item.hashtags,
          aiCaption: item.aiCaption,
        },
      });
    };

    const inList = content.find((c) => c.id === openId);
    if (inList) {
      openPublish(inList);
    } else {
      api.get(`/api/v1/content/${openId}`)
        .then((res) => openPublish(res.data?.data))
        .catch(() => toast.error('That content could not be found'));
    }

    // Clear the param so a refresh doesn't reopen the wizard
    const next = new URLSearchParams(searchParams);
    next.delete('open');
    setSearchParams(next, { replace: true });
  }, [searchParams, content, setSearchParams]);

  // ── Idea Studio bridge: open the create flow pre-filled from an idea ──────────
  // Idea Studio's "Use this" navigates here with { ideaDraft: {title, description,
  // platform} } instead of creating a stranded VIDEO stub. We open the upload modal
  // pre-filled so the creator picks the real format and attaches media (or publishes
  // a text post as-is). State is consumed once, then cleared so refresh/back won't reopen.
  const ideaPrefillHandled = useRef(false);
  useEffect(() => {
    if (ideaPrefillHandled.current) return;
    const draft = location.state?.ideaDraft;
    if (!draft) return;
    ideaPrefillHandled.current = true;
    setUploadPrefill(draft);
    setShowUploadModal(true);
    navigate(location.pathname + location.search, { replace: true, state: null });
  }, [location, navigate]);

  // ── Mutations ────────────────────────────────────────────────────────────────
  const handleCreated = (item) => {
    setContent(prev => [item, ...prev].slice(0, PAGE_SIZE));
    setTotalElements(t => t + 1);
    if (totalPages === 0) setTotalPages(1);
  };

  const handleUpdated = (updated) =>
    setContent(prev => prev.map(c => c.id === updated.id ? updated : c));

  const handleDeleted = (deletedId) => {
    setContent(prev => prev.filter(c => c.id !== deletedId));
    setTotalElements(t => Math.max(0, t - 1));
  };

  const clearFilters = () => {
    setSearchInput('');
    setQuery(q => ({ ...q, search: '', status: '', contentType: '', platform: '' }));
    setPage(0);
  };

  const hasActiveFilters = !!(query.search || query.status || query.contentType || query.platform);

  // ── Pagination page-number list (with ellipsis) ───────────────────────────
  const pageNumbers = (() => {
    const delta = 2;
    const nums = [];
    for (let i = 0; i < totalPages; i++) {
      if (i === 0 || i === totalPages - 1 || (i >= page - delta && i <= page + delta)) {
        nums.push(i);
      } else if (nums[nums.length - 1] !== '…') {
        nums.push('…');
      }
    }
    return nums;
  })();

  // Shared empty state (desktop table + mobile card list)
  const emptyState = (
    <div className="py-16 flex flex-col items-center justify-center text-center">
      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-pink-50 to-violet-50 dark:from-pink-900/20 dark:to-violet-900/20 flex items-center justify-center mb-4">
        <DocumentTextIcon className="w-7 h-7 text-violet-400" />
      </div>
      {hasActiveFilters ? (
        <>
          <p className="text-base font-medium text-gray-600 dark:text-gray-400 mb-1">No results found</p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mb-3">Try different search terms or clear your filters.</p>
          <button onClick={clearFilters} className="text-sm text-violet-600 dark:text-violet-400 underline hover:no-underline">
            Clear filters
          </button>
        </>
      ) : (
        <>
          <p className="text-base font-medium text-gray-600 dark:text-gray-400 mb-1">No content yet</p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mb-4">
            Upload your first piece of content and distribute it across multiple platforms at once.
          </p>
          <button onClick={() => setShowUploadModal(true)} className="btn flex items-center gap-2 text-white bg-gradient-to-r from-pink-500 to-violet-600 hover:from-pink-600 hover:to-violet-700 shadow-md">
            <PlusIcon className="w-4 h-4" />
            Upload Content
          </button>
        </>
      )}
    </div>
  );

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Content Management</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {totalElements > 0
              ? `${totalElements.toLocaleString()} item${totalElements !== 1 ? 's' : ''} across all platforms`
              : 'Manage your content across all platforms'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleRefresh}
            disabled={loading}
            title="Refresh"
            className="p-2.5 rounded-xl border border-gray-200 dark:border-dark-700 text-gray-500 dark:text-gray-400
              hover:bg-gray-100 dark:hover:bg-dark-800 hover:text-gray-700 dark:hover:text-gray-200
              transition-colors disabled:opacity-50"
          >
            <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={() => setShowUploadModal(true)} className="btn flex items-center gap-2 text-white bg-gradient-to-r from-pink-500 to-violet-600 hover:from-pink-600 hover:to-violet-700 shadow-md">
            <PlusIcon className="w-5 h-5" />
            New Content
          </button>
        </div>
      </div>

      <div className="card p-6">

        {/* ── Filter Toolbar ── */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          {/* Search */}
          <div className="relative flex-1 min-w-0">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Search by title…"
              className="input pl-9 w-full min-h-[44px] sm:min-h-0"
            />
            {searchInput && (
              <button
                onClick={() => setSearchInput('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Status filter */}
          <select value={query.status} onChange={e => setFilter('status', e.target.value)} className="input w-full sm:w-40 min-h-[44px] sm:min-h-0">
            <option value="">All statuses</option>
            <option value="DRAFT">Draft</option>
            <option value="PUBLISHED">Published</option>
            <option value="PARTIALLY_PUBLISHED">Partial</option>
            <option value="SCHEDULED">Scheduled</option>
            <option value="PROCESSING">Processing</option>
            <option value="FAILED">Failed</option>
          </select>

          {/* Content type filter */}
          <select value={query.contentType} onChange={e => setFilter('contentType', e.target.value)} className="input w-full sm:w-36 min-h-[44px] sm:min-h-0">
            <option value="">All types</option>
            <option value="VIDEO">Video</option>
            <option value="IMAGE">Image</option>
            <option value="TEXT">Text</option>
            <option value="REEL">Reel</option>
            <option value="SHORT">Short</option>
            <option value="STORY">Story</option>
            <option value="CAROUSEL">Carousel</option>
          </select>

          {/* Platform filter (active connections only) */}
          {connections.length > 0 && (
            <select value={query.platform} onChange={e => setFilter('platform', e.target.value)} className="input w-full sm:w-40 min-h-[44px] sm:min-h-0">
              <option value="">All platforms</option>
              {connections.map(c => {
                const meta = PLATFORM_META[c.platform] || {};
                return (
                  <option key={c.platform} value={c.platform}>
                    {meta.label || c.platform}
                  </option>
                );
              })}
            </select>
          )}

          {/* Sort */}
          <select value={query.sort} onChange={e => setFilter('sort', e.target.value)} className="input w-full sm:w-48 min-h-[44px] sm:min-h-0">
            <option value="createdAt,desc">Newest first</option>
            <option value="createdAt,asc">Oldest first</option>
            <option value="title,asc">Title A–Z</option>
            <option value="title,desc">Title Z–A</option>
            <option value="publishedAt,desc">Published (newest)</option>
          </select>
        </div>

        {/* Active-filters indicator */}
        {hasActiveFilters && (
          <div className="flex items-center gap-2 mb-3 text-xs">
            <FunnelIcon className="w-3.5 h-3.5 text-fuchsia-500" />
            <span className="text-violet-600 dark:text-violet-400 font-medium">Filters active</span>
            <button onClick={clearFilters} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 underline">
              Clear all
            </button>
          </div>
        )}

        {/* ── Mobile card list (below md) ── */}
        <div className="md:hidden">
          {loading ? (
            <div className="space-y-3">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="rounded-xl border border-gray-100 dark:border-dark-800 p-3">
                  <div className="flex gap-3">
                    <div className="h-14 w-20 bg-gray-200 dark:bg-dark-700 rounded animate-pulse flex-shrink-0" />
                    <div className="flex-1 min-w-0 space-y-2">
                      <div className="h-4 bg-gray-200 dark:bg-dark-700 rounded animate-pulse w-3/4" />
                      <div className="h-3 bg-gray-100 dark:bg-dark-700 rounded animate-pulse w-1/2" />
                    </div>
                  </div>
                  <div className="mt-3 h-8 bg-gray-100 dark:bg-dark-700 rounded animate-pulse w-32" />
                </div>
              ))}
            </div>
          ) : content.length === 0 ? (
            emptyState
          ) : (
            <div className="space-y-3">
              {content.map((item) => {
                const platforms = item.targetPlatforms?.length
                  ? item.targetPlatforms
                  : item.targetPlatform ? [item.targetPlatform] : [];
                return (
                  <div
                    key={item.id}
                    className="rounded-xl border border-gray-100 dark:border-dark-800 p-3 bg-white dark:bg-dark-900"
                  >
                    <div className="flex items-start gap-3">
                      {/* Thumbnail */}
                      {cardThumb(item) ? (
                        <img
                          src={cardThumb(item)}
                          alt=""
                          className="h-14 w-20 object-cover rounded flex-shrink-0 bg-gray-100 dark:bg-dark-700"
                        />
                      ) : (
                        <div className="h-14 w-20 rounded flex-shrink-0 bg-gray-100 dark:bg-dark-700 flex items-center justify-center">
                          {['VIDEO','REEL','SHORT'].includes(item.contentType)
                            ? <VideoCameraIcon className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                            : <PhotoIcon className="w-5 h-5 text-gray-400 dark:text-gray-500" />}
                        </div>
                      )}
                      {/* Title + meta stacked */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm font-medium text-gray-900 dark:text-white break-words min-w-0">
                            {item.title}
                          </p>
                          <span className="flex-shrink-0"><StatusBadge status={item.status} /></span>
                        </div>
                        <div className="mt-1.5"><PlatformChips platforms={platforms} jobs={item.platformJobs || []} /></div>
                        {needsMedia(item) && <div className="mt-1.5"><NeedsMediaChip /></div>}
                        <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                          {item.contentType}
                          {item.durationSeconds != null && (
                            <> · <span className="tabular-nums">
                              {Math.floor(item.durationSeconds / 60)}:{String(item.durationSeconds % 60).padStart(2, '0')}
                            </span></>
                          )}
                          {' · '}{(item.views ?? 0).toLocaleString()} views
                          {item.publishedAt && <> · {new Date(item.publishedAt).toLocaleDateString()}</>}
                        </p>
                      </div>
                    </div>
                    {/* Actions — same buttons, always visible, wrapped under the text */}
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <ContentActions
                        item={item}
                        onEdit={(itm, action) => {
                          if (action === 'schedule') setSchedulingItem(itm);
                          else setEditingItem(itm);
                        }}
                        onPreview={setPreviewItem}
                        onUpdated={handleUpdated}
                        onDeleted={handleDeleted}
                        onPublish={setPublishModalData}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Table (md and up) ── */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-sm text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-dark-700">
                <th className="pb-3 font-medium">Title</th>
                <th className="pb-3 font-medium">Platforms</th>
                <th className="pb-3 font-medium">Type</th>
                <th className="pb-3 font-medium">Duration</th>
                <th className="pb-3 font-medium">Status</th>
                <th className="pb-3 font-medium">Views</th>
                <th className="pb-3 font-medium">Published</th>
                <th className="pb-3 font-medium w-20"></th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {loading ? (
                [...Array(6)].map((_, i) => (
                  <tr key={i} className="border-b border-gray-100 dark:border-dark-800">
                    <td className="py-4 pr-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-16 bg-gray-200 dark:bg-dark-700 rounded animate-pulse flex-shrink-0" />
                        <div className="space-y-1.5">
                          <div className="h-4 bg-gray-200 dark:bg-dark-700 rounded animate-pulse w-36" />
                          <div className="h-3 bg-gray-100 dark:bg-dark-700 rounded animate-pulse w-24" />
                        </div>
                      </div>
                    </td>
                    <td className="py-4 pr-4"><div className="h-5 bg-gray-200 dark:bg-dark-700 rounded-full animate-pulse w-24" /></td>
                    <td className="py-4 pr-4"><div className="h-4 bg-gray-200 dark:bg-dark-700 rounded animate-pulse w-14" /></td>
                    <td className="py-4 pr-4"><div className="h-4 bg-gray-200 dark:bg-dark-700 rounded animate-pulse w-10" /></td>
                    <td className="py-4 pr-4"><div className="h-5 bg-gray-200 dark:bg-dark-700 rounded-full animate-pulse w-20" /></td>
                    <td className="py-4 pr-4"><div className="h-4 bg-gray-200 dark:bg-dark-700 rounded animate-pulse w-10" /></td>
                    <td className="py-4 pr-4"><div className="h-4 bg-gray-200 dark:bg-dark-700 rounded animate-pulse w-20" /></td>
                    <td className="py-4"></td>
                  </tr>
                ))
              ) : content.length === 0 ? (
                <tr>
                  <td colSpan={8}>{emptyState}</td>
                </tr>
              ) : (
                content.map((item) => {
                  const platforms = item.targetPlatforms?.length
                    ? item.targetPlatforms
                    : item.targetPlatform ? [item.targetPlatform] : [];
                  return (
                    <tr
                      key={item.id}
                      className="border-b border-gray-100 dark:border-dark-800 last:border-0 hover:bg-gray-50 dark:hover:bg-dark-800/50 transition-colors"
                    >
                      <td className="py-4 text-gray-900 dark:text-white font-medium">
                        <div className="flex items-center gap-3">
                          {cardThumb(item) ? (
                            <img
                              src={cardThumb(item)}
                              alt=""
                              className="h-10 w-16 object-cover rounded flex-shrink-0 bg-gray-100 dark:bg-dark-700"
                            />
                          ) : (
                            <div className="h-10 w-16 rounded flex-shrink-0 bg-gray-100 dark:bg-dark-700 flex items-center justify-center">
                              {['VIDEO','REEL','SHORT'].includes(item.contentType)
                                ? <VideoCameraIcon className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                                : <PhotoIcon className="w-5 h-5 text-gray-400 dark:text-gray-500" />}
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="truncate">{item.title}</p>
                            {needsMedia(item) && <div className="mt-1"><NeedsMediaChip /></div>}
                          </div>
                        </div>
                      </td>
                      <td className="py-4"><PlatformChips platforms={platforms} jobs={item.platformJobs || []} /></td>
                      <td className="py-4 text-gray-500 dark:text-gray-400 text-xs">{item.contentType}</td>
                      <td className="py-4 text-gray-500 dark:text-gray-400 text-xs tabular-nums">
                        {item.durationSeconds != null
                          ? `${Math.floor(item.durationSeconds / 60)}:${String(item.durationSeconds % 60).padStart(2, '0')}`
                          : <span className="text-gray-300 dark:text-gray-600">—</span>}
                      </td>
                      <td className="py-4"><StatusBadge status={item.status} /></td>
                      <td className="py-4 text-gray-700 dark:text-gray-300">{(item.views ?? 0).toLocaleString()}</td>
                      <td className="py-4 text-gray-500 dark:text-gray-400">
                        {item.publishedAt
                          ? new Date(item.publishedAt).toLocaleDateString()
                          : <span className="text-gray-300 dark:text-gray-600">—</span>}
                      </td>
                      <td className="py-4">
                        <ContentActions
                          item={item}
                          onEdit={(itm, action) => {
                            if (action === 'schedule') setSchedulingItem(itm);
                            else setEditingItem(itm);
                          }}
                          onPreview={setPreviewItem}
                          onUpdated={handleUpdated}
                          onDeleted={handleDeleted}
                          onPublish={setPublishModalData}
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ── */}
        {!loading && totalElements > 0 && (
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3
            pt-4 mt-2 border-t border-gray-200 dark:border-dark-700">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Showing{' '}
              <span className="font-medium text-gray-700 dark:text-gray-300">
                {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, totalElements)}
              </span>
              {' '}of{' '}
              <span className="font-medium text-gray-700 dark:text-gray-300">
                {totalElements.toLocaleString()}
              </span>{' '}items
            </p>

            {totalPages > 1 && (
              <div className="flex items-center gap-1 overflow-x-auto max-w-full">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="flex-shrink-0 flex items-center justify-center w-11 h-11 md:w-auto md:h-auto md:p-1.5
                    rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-700
                    disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  title="Previous page"
                >
                  <ChevronLeftIcon className="w-4 h-4" />
                </button>

                {pageNumbers.map((p, idx) =>
                  p === '…' ? (
                    <span key={`e${idx}`} className="flex-shrink-0 px-1 text-gray-400 text-sm select-none">…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`flex-shrink-0 min-w-[44px] h-11 md:min-w-[32px] md:h-8 rounded-lg text-sm font-medium transition-colors
                        ${p === page
                          ? 'bg-gradient-to-r from-pink-500 to-violet-600 text-white shadow-md'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-700'}`}
                    >
                      {p + 1}
                    </button>
                  )
                )}

                <button
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page === totalPages - 1}
                  className="flex-shrink-0 flex items-center justify-center w-11 h-11 md:w-auto md:h-auto md:p-1.5
                    rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-700
                    disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  title="Next page"
                >
                  <ChevronRightIcon className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <AnimatePresence>
        {showUploadModal && (
          <UploadModal
            prefill={uploadPrefill}
            onClose={() => { setShowUploadModal(false); setUploadPrefill(null); }}
            onCreated={handleCreated}
            onReadyToPublish={(data) => {
              setShowUploadModal(false);
              setUploadPrefill(null);
              setPublishModalData(data);
            }}
            onDesignStory={(payload) => {
              setShowUploadModal(false);
              setUploadPrefill(null);
              setStoryDesign(payload);
            }}
          />
        )}
        {storyDesign && (
          <StoryStudio
            item={storyDesign.item}
            onClose={() => setStoryDesign(null)}
            onSaved={(updated) => {
              const d = storyDesign;
              setStoryDesign(null);
              handleCreated(updated);
              // Continue straight to publish settings with the designed story.
              setPublishModalData({
                contentId: d.item.id,
                platforms: d.platforms,
                connectionIds: d.connectionIds,
                content: d.content,
                fileDuration: d.fileDuration || null,
              });
            }}
          />
        )}
        {editingItem && (
          <EditModal
            key={editingItem.id}
            item={editingItem}
            onClose={() => setEditingItem(null)}
            onUpdated={(updated) => { handleUpdated(updated); setEditingItem(null); }}
          />
        )}
        {previewItem && (
          <PreviewModal
            key={`preview-${previewItem.id}`}
            item={previewItem}
            onClose={() => setPreviewItem(null)}
          />
        )}
        {schedulingItem && (
          <ScheduleModal
            key={`sched-${schedulingItem.id}`}
            item={schedulingItem}
            onClose={() => setSchedulingItem(null)}
            onScheduled={(updated) => { handleUpdated(updated); setSchedulingItem(null); }}
          />
        )}
        {publishModalData && (
          <PublishSettingsModal
            key={`publish-${publishModalData.contentId}`}
            contentId={publishModalData.contentId}
            platforms={publishModalData.platforms}
            content={publishModalData.content}
            fileDuration={publishModalData.fileDuration || null}
            onPublished={(published) => {
              handleCreated(published);
              setPublishModalData(null);
            }}
            onTargetsUpdated={(updated) => { if (updated) handleUpdated(updated); }}
            onDiscarded={(discardedId) => {
              handleDeleted(discardedId);
              setPublishModalData(null);
            }}
            onClose={() => setPublishModalData(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ContentPage;
